import { ItemsPerPage, Table } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { Row } from 'react-table'

import { SortDirection } from '../../../../../../enums/sortDirection'
import { TableIds } from '../../../../../../enums/tableIds'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { ImportedInvoicesSortProperty } from '../../../../enums/importedInvoicesSortProperty'
import { ImportedInvoicesTableRow } from '../../../../types/importedInvoicesTableRow'
import { useImportedInvoicesListData } from '../../context/importedInvoicesListData'
import { useImportedInvoicesListFilters } from '../../context/importedInvoicesListFilters'
import { ImportedInvoicesTableEmptyRowInfo } from './elements/ImportedInvoicesTableEmptyRowInfo'
import { useImportedInvoicesTableColumns } from './hooks/useImportedInvoicesTableColumns'

interface ImportedInvoicesTableProps {
  tableId: TableIds
}

export const ImportedInvoicesTable = ({ tableId }: ImportedInvoicesTableProps): ReactElement => {
  const [{ pageSize, sortProperty, sortDirection }, setFilters] = useImportedInvoicesListFilters()
  const { invoices, isLoading, isReviewEnabled, onSelectRow, pagination } = useImportedInvoicesListData()
  const { columns } = useImportedInvoicesTableColumns()

  const sortedColumnId = useMemo(
    () => getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc),
    [sortDirection, sortProperty],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setFilters({
        page: 1,
        sortProperty: columnId as ImportedInvoicesSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setFilters],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters],
  )

  const handleRowClick = useCallback(
    (row: Row<ImportedInvoicesTableRow>) => {
      onSelectRow(row.original)
    },
    [onSelectRow],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setFilters({
        page: 1,
        pageSize: itemsPerPage,
      })
    },
    [setFilters],
  )

  return (
    <Table
      columns={columns}
      currentPage={pagination?.page}
      data={invoices}
      emptyRowInfo={<ImportedInvoicesTableEmptyRowInfo />}
      id={tableId}
      isLoading={isLoading}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      // `undefined` while the drawer is disabled, so the DS table keeps rendering
      // non-clickable rows.
      onRowClick={isReviewEnabled ? handleRowClick : undefined}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      totalPageItems={pagination?.total}
      withColumnsFiltering={false}
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
